import { ASNDocumentType } from '../../lib/api';
import type { ASNColumnFormatColumn } from '../../lib/api';

export interface ASNFormData {
  asn_number: string;
  schedules: number[];
  courier: string;
  tracking_number: string;
  method_of_transport: string;
  weight_kg: string;
  distance_km: string;
  expected_delivery_date: string;
  notes: string;
}

export interface ASNScheduleDetail {
  id: number;
  po_number?: string;
  po_id?: number;
  po_line_id?: number;
  line_number?: number;
  /** True SAP line index (POR1.LineNum); null until SAP sends it. Used by Produmax export. */
  sap_order_row?: number | null;
  part_number?: string;
  /** CR-7308: the PART MASTER's name for the part — what sits beside Part Number. */
  part_description?: string;
  /** The text typed on the order line. Still returned; no longer the column
   *  beside Part Number, because that column is about the part. */
  description?: string;
  schedule_qty?: string | number;
  quantity?: number;
  shipped_qty?: string | number;
  balance_qty?: string;
  unit_of_measure?: string;
  status?: string;
  schedule_date?: string;
  due_date?: string;
  supplier_name?: string;
  customer_name?: string;
  grn_number?: string;
  created_at?: string;
  updated_at?: string;
  shipment_number?: string;
  tracking_number?: string;
  po_line_details?: {
    po_details?: { po_number: string };
    line_number?: number;
    part_number?: string;
    part_description?: string;
    description?: string;
    unit_of_measure?: string;
  };
}

export interface ASNData {
  id: number;
  asn_number: string;
  status: string;
  /** CR-7142: set when the customer acknowledges. Load-bearing for a direct
   *  ASN, which stays at status 'submitted' after acknowledgement — so status
   *  alone can no longer tell you whether it has been acknowledged. */
  customer_acknowledgment_date?: string | null;
  /** CR-7142: true when every delivery line on this ASN is direct. Derived on
   *  the backend from the lines; the ASN list has no line detail to derive it
   *  from. Governs whether the load skips In Transit entirely. */
  is_direct_delivery?: boolean;
  courier?: string;
  /** CR-7732: the name behind courier='other' — a haulier, typically. */
  courier_other?: string;
  tracking_number?: string;
  // CR-6160: live courier tracking (paid module; provider-managed)
  tracking_id?: string;
  courier_delivery_status?: string;
  tracking_raw_status?: string;
  tracking_last_synced_at?: string | null;
  tracking_checkpoints?: Array<{ timestamp?: string | null; status?: string; location?: string; message?: string }>;
  tracking_origin?: string;
  tracking_destination?: string;
  tracking_estimated_delivery?: string;
  invoice_number?: string;
  method_of_transport?: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  weight_kg?: string | number;
  /** CR-6291: live gross weight for call-off ASNs; shown as the header weight. */
  computed_weight_kg?: string | number | null;
  distance_km?: string | number;
  carbon_emission_kg?: string | number;
  calculated_carbon_emission?: string | number;
  notes?: string;
  supplier_details?: { name: string };
  customer_details?: { name: string };
  schedules_details?: ASNScheduleDetail[];
  source_type?: string;
  call_off?: number | null;
  call_off_reference?: string;
  delivery_lines_details?: ASNDeliveryLineDetail[];
  /** CR-6322: customer's saved delivery-note column layout (ordered); null for non-call-off ASNs. */
  column_format?: ASNColumnFormatColumn[] | null;
  /** CR-7194: whether the configurable CSV export is available for this ASN.
   *  Resolved server-side from the ASN's CUSTOMER, so a supplier viewer gets it
   *  without needing the buying side's feature flag on its own company. */
  csv_export_enabled?: boolean;
}

export interface ASNDeliveryLineDetail {
  id: number;
  delivery_number: string;
  part_number: string;
  part_description: string;
  qty_released: string;
  delivery_option: string;
  delivery_address: string;
  sales_order: string;
  po_order_number: string;
  po_line_number: string;
  /** True SAP line index (POR1.LineNum); null until SAP sends it. Used by Produmax export. */
  sap_order_row?: number | null;
  /** CR-6173: per-pallet quantity for this part; null when the part has no pallet qty. */
  pallet_qty?: string | null;
  /** CR-6291: gross weight per unit (kg) from the Parts Master; null when unset. */
  unit_weight_kg?: string | null;
  /** CR-6291: commodity (HS) code from the Parts Master — for export shipments. */
  commodity_code?: string | null;
  call_off_reference: string;
  /** CR-5763: per-line released date (ISO datetime); auto-set at line creation/import. */
  date_released?: string | null;
  /** CR-7194: price per unit from the linked call-off line. Null for a
   *  supplier-import ASN (no call-off line), and absent entirely for a
   *  logistics user, who is never sent price data. */
  unit_price?: string | null;
}

export interface ASNModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ASNFormData | Record<string, string | number | boolean>) => Promise<void>;
  /**
   * When set, the wizard opens on the delivery-lines source and auto-selects
   * every available delivery line whose call-off matches this ID. Used when
   * the supplier launches Create ASN from a specific call-off.
   */
  initialCallOffId?: number;
  /**
   * CR-6038: when set, the wizard opens on the delivery-lines source and
   * pre-selects these delivery-line IDs. Used by the standalone Import &
   * Allocate workflow to route imported lines into a new ASN.
   */
  initialDeliveryLineIds?: number[];
  /**
   * CR-7367: narrows `initialCallOffId`'s auto-selection to one destination.
   * A mixed call-off ships as two ASNs — one warehouse, one direct — so each
   * Create-ASN button on the call-off passes the side it stands for and the
   * wizard opens with only those lines ticked. Left unset, every line on the
   * call-off is selected, which is what a single-destination load wants.
   */
  initialDeliveryOption?: 'warehouse' | 'direct';
}

export interface ASNDetailModalProps extends ASNModalProps {
  asn: ASNData;
}

// Document type labels for display
export const DOCUMENT_TYPE_LABELS: Record<ASNDocumentType, string> = {
  cofc: 'Certificate of Conformance',
  fair: 'First Article Inspection Report',
  delivery_note: 'Delivery Note',
  material_cert: 'Material Certificate',
  commercial_invoice: 'Commercial Invoice',
  packing_list: 'Packing List',
  export_declaration: 'Export Clearance Declaration',
  other: 'Other',
};
